import styled from 'styled-components';
import { Modal } from '../Modal';
import { useState } from 'react';
import FileUploadPopUp from './FileUploadPopUp';
import {
  useHotelQuestionnaireSteps,
  useNavigationConfirmation,
} from '../../../hooks';
import { truncateFilename } from './fileUtils';
import { getSourceGroupForStep } from '../../../utils/stepSourceMapping';
import { useQuestionRefs } from '../../../contexts/QuestionRefsContext';
import { NavigationConfirmModal } from '../NavigationConfirmModal';

export const NoticeContainer = styled.div`
  padding: 1.25rem;
  border-radius: 1rem;
  border: 1px solid var(--color-primary-200);
  background-color: var(--color-bg-2);
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    padding: var(--spacing-3);
    gap: var(--spacing-2);
    font-size: var(--font-size-14);
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StyledText = styled.p<{ variant?: 'primary' | 'error' | 'default' }>`
  color: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return 'var(--color-primary-600)';
      case 'error':
        return 'var(--color-error)';
      default:
        return 'inherit';
    }
  }};

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;

const UnderlinedSpan = styled.span`
  text-decoration: underline;
  cursor: pointer;
  text-underline-offset: 2px;
`;

/**
 * FileUploadNotice component displays a notice regarding file uploads for a specific question.
 * @param param0 - Props for the component
 * @param0.questionId - The ID of the question related to the file upload
 * @param0.isReadOnly - Flag indicating if the questionnaire is in read-only mode
 * @param0.uploadedFilenames - List of filenames that have been uploaded
 * @param0.showImportantNotice - Flag to show an important notice
 * @returns JSX.Element
 */
export default function FileUploadNotice({
  questionId,
  isReadOnly,
  uploadedFilenames,
  showImportantNotice = false,
}: {
  questionId?: string;
  isReadOnly?: boolean;
  uploadedFilenames?: string[];
  showImportantNotice?: boolean;
}) {
  const pathName = window.location.pathname;
  const isSampleQuestionnaire = pathName.includes('sample-questionnaire');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { goToSourcesWithGroup, currentStepKey } = useHotelQuestionnaireSteps();
  const { scrollToQuestion } = useQuestionRefs();
  const {
    showConfirmModal,
    pendingNavigation,
    isConfirmingNavigation,
    isSavingNavigation,
    requestNavigation,
    cancelNavigation,
    confirmNavigation,
    saveAndNavigate,
  } = useNavigationConfirmation();

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };
  const handleModalClose = (refetch = false) => {
    setIsModalOpen(false);
  };

  const handleGoToSources = async () => {
    // Get the target source group based on current step
    const targetGroup = currentStepKey
      ? getSourceGroupForStep(currentStepKey)
      : null;

    const canNavigate = requestNavigation('sources', 'Sources');

    if (!canNavigate) {
      return;
    }

    goToSourcesWithGroup(targetGroup || undefined);
    // Scroll to the question after navigating
    if (questionId) {
      setTimeout(() => {
        scrollToQuestion(questionId);
      }, 500);
    }
  };

  return (
    <NoticeContainer>
      {/* {showImportantNotice && (
        <CircleAlert size={20} color="var(--color-primary-500)" />
      )} */}
      <ContentWrapper>
        {/* {showImportantNotice && (
          <StyledText variant="primary">
            <BoldSpan>IMPORTANT </BoldSpan>- please ensure your answers to other
            sections of the questionnaire reflect the achievements within this
            certification and any subsequent advancements made.
          </StyledText>
        )} */}
        {uploadedFilenames && uploadedFilenames?.length > 0 ? (
          <StyledText variant="primary">
            <span className=''>
            {uploadedFilenames
              .map(filename => truncateFilename(filename))
              .join(', ')}{' '}
            </span>
            <span className='ml-1 font-medium'>
              view all files in{' '}
              <UnderlinedSpan onClick={handleGoToSources} className=' '>
                SOURCES
              </UnderlinedSpan>
            </span>
          </StyledText>
        ) : (
          <StyledText variant="error">
            You will be asked to provide verification for this answer - add
            sources{' '}
            <UnderlinedSpan
              onClick={handleModalOpen}
              aria-disabled={isReadOnly}
              style={{
                pointerEvents:
                  isReadOnly || isSampleQuestionnaire ? 'none' : 'auto',
              }} //disable pointer events if read-only or sample questionnaire
            >
              HERE
            </UnderlinedSpan>{' '}
            now, or add later by clicking Sources on the nav bar
          </StyledText>
        )}
      </ContentWrapper>
      <Modal isOpen={isModalOpen} onClose={handleModalClose} title="" size="xl">
        <FileUploadPopUp
          onClose={handleModalClose}
          questionId={questionId}
          maxFiles={20}
          maxFileSize={20}
          attachments={[]}
        />
      </Modal>
      <NavigationConfirmModal
        isOpen={showConfirmModal}
        onClose={cancelNavigation}
        onConfirm={async () => {
          await confirmNavigation();
        }}
        onSave={async () => {
          await saveAndNavigate();
        }}
        targetStep={pendingNavigation?.stepName}
        isConfirmingNavigation={isConfirmingNavigation}
        isSavingNavigation={isSavingNavigation}
      />
    </NoticeContainer>
  );
}
